import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetFolderContent } from '@/api/community-essentials'
import { Page } from '@/components/layout/page'
import { RadialLoader } from '@/components/ui/loaders/radial-loader'
import { Typography } from '@/components/ui/typography'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectUserBuilding } from '@/reducers/userDataReducer'

import { Breadcrumbs } from './breadcrumbs/breadcrumbs'
import { ClientEssentialItem } from './client-essential-item/client-essential-item'
import { ClientFolderItem } from './client-folder-item/client-folder-item'
import { useFolderNavigation } from './community-essentials.hooks'
import * as S from './community-essentials.styled'
import { EmptyState } from './empty-state/empty-state'

export const ClientCommunityEssentialsPage = () => {
  const { t } = useTranslation(['community-essentials'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const userBuilding = useSelector(selectUserBuilding) as { id?: number } | null
  const buildingId = activeBuilding?.id ?? userBuilding?.id

  const {
    folderPath,
    currentFolder,
    isRoot,
    navigateToFolder,
    navigateBack,
    navigateToBreadcrumb,
  } = useFolderNavigation()

  const { data, isError, isPlaceholderData } = useGetFolderContent({
    buildingId,
    folderId: currentFolder.id,
  })
  const { essentials = [], folders = [] } = data ?? {}

  const visibleFolders = useMemo(
    () =>
      folders.filter((folder) => {
        const hasEssentials = (folder.essentials?.length ?? 0) > 0
        const hasSubfolders = (folder.subfolders?.length ?? 0) > 0
        return hasEssentials || hasSubfolders
      }),
    [folders],
  )

  const hasContent = visibleFolders.length > 0 || essentials.length > 0

  if (isPlaceholderData) {
    return (
      <Page>
        <S.Toolbar>
          <Typography size="heading-large" weight="semibold">
            {t('community-essentials:title')}
          </Typography>
        </S.Toolbar>
        <S.LoaderWrapper>
          <RadialLoader />
        </S.LoaderWrapper>
      </Page>
    )
  }

  if (isError) {
    return (
      <Page>
        <S.Toolbar>
          <Typography size="heading-large" weight="semibold">
            {t('community-essentials:title')}
          </Typography>
        </S.Toolbar>
        <Typography color="red500">{t('community-essentials:loadError')}</Typography>
      </Page>
    )
  }

  return (
    <Page>
      <S.Toolbar>
        {isRoot ? (
          <Typography size="heading-large" weight="semibold">
            {t('community-essentials:title')}
          </Typography>
        ) : (
          <Breadcrumbs
            folderPath={folderPath}
            onBack={navigateBack}
            onNavigate={navigateToBreadcrumb}
          />
        )}
      </S.Toolbar>

      {hasContent ? (
        <S.EssentialsList>
          {visibleFolders.map((folder) => (
            <ClientFolderItem key={folder.id} folder={folder} onNavigate={navigateToFolder} />
          ))}
          {essentials.map((item) => (
            <ClientEssentialItem key={item.id} item={item} />
          ))}
        </S.EssentialsList>
      ) : (
        <EmptyState insideFolder={!isRoot} />
      )}
    </Page>
  )
}
